import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import { 
  Download,
  X,
  FileText,
  FileSpreadsheet,
  Presentation,
  Edit2,
  History,
  Calendar,
  User,
  FileEdit
} from 'lucide-react';

// Import editors
import DocumentEditor from './DocumentEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentPreviewDialog = ({ 
  isOpen, 
  onClose, 
  document, 
  userId,
  userName = "User",
  onDocumentUpdate,
  onDocumentDelete,
  onDocumentRename
}) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [showVersions, setShowVersions] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    if (isOpen && document) {
      setPreviewUrl(document.url);
      setNewTitle(document.title);
    }
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [isOpen, document]);

  const handleDownload = async () => {
    if (!document) return;

    try {
      // Use direct link download to bypass fetch interceptors
      const downloadUrl = `${BACKEND_URL}/api/documents/${document.key}/download`;
      
      const a = window.document.createElement('a');
      a.style.display = 'none';
      a.href = downloadUrl;
      a.download = document.title;
      a.target = '_blank';
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);

      toast({
        title: "Success",
        description: "Document downloaded successfully"
      });
    } catch (error) {
      console.error('Error downloading document:', error);
      toast({
        title: "Error",
        description: `Failed to download document: ${error.message}`,
        variant: "destructive"
      });
    }
  };

  const handleRename = async () => {
    if (!document || !newTitle.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/documents/${document.key}/rename?user_id=${userId}&new_title=${encodeURIComponent(newTitle.trim())}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Success",
          description: "Document renamed successfully"
        });
        setIsRenaming(false);
        if (onDocumentRename) {
          onDocumentRename(document.key, newTitle.trim());
        }
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to rename document');
      }
    } catch (error) {
      console.error('Error renaming document:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to rename document",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!document) return;

    if (!window.confirm(`Are you sure you want to delete "${document.title}"?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/documents/${document.key}?user_id=${userId}`,
        {
          method: 'DELETE'
        }
      );

      if (response.ok) {
        toast({
          title: "Success",
          description: "Document deleted successfully"
        });
        if (onDocumentDelete) {
          onDocumentDelete(document.key);
        }
        onClose();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete document",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileType) => {
    const type = fileType?.toLowerCase();
    if (['docx', 'doc', 'odt'].includes(type)) {
      return <FileText className="h-5 w-5 text-blue-600" />;
    } else if (['xlsx', 'xls', 'ods', 'csv'].includes(type)) {
      return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
    } else if (['pptx', 'ppt', 'odp'].includes(type)) {
      return <Presentation className="h-5 w-5 text-orange-600" />;
    }
    return <FileText className="h-5 w-5 text-gray-600" />;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading document...</p>
          </div>
        </div>
      );
    }

    if (!document) return null;

    const fileType = document.file_type?.toLowerCase();
    const supportedTypes = ['docx', 'doc', 'odt', 'xlsx', 'xls', 'ods', 'pptx', 'ppt', 'odp'];

    // Default preview/placeholder for all types
    const getIcon = () => {
      if (['docx', 'doc', 'odt'].includes(fileType)) {
        return <FileText className="h-16 w-16 text-blue-500 mx-auto mb-4" />;
      } else if (['xlsx', 'xls', 'ods', 'csv'].includes(fileType)) {
        return <FileSpreadsheet className="h-16 w-16 text-green-500 mx-auto mb-4" />;
      } else if (['pptx', 'ppt', 'odp'].includes(fileType)) {
        return <Presentation className="h-16 w-16 text-orange-500 mx-auto mb-4" />;
      }
      return <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />;
    };

    return (
      <div className="bg-gray-50 rounded-lg overflow-hidden" style={{ height: '600px' }}>
        <div className="text-center py-8 px-4">
          {getIcon()}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {document.title}
          </h3>
          
          <p className="text-sm text-gray-600 mb-3">
            {fileType.toUpperCase()} Document
          </p>
          {supportedTypes.includes(fileType) && (
            <div className="max-w-md mx-auto mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                💡 Collaborative editing will be implemented soon
              </p>
            </div>
          )}
          {!supportedTypes.includes(fileType) && (
            <p className="text-sm text-gray-500 mb-4">
              Download to view this document
            </p>
          )}
          
          <Button 
            onClick={handleDownload} 
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Document
          </Button>
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown';
    }
  };

  if (!document) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[90vh] flex flex-col p-0">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 flex-1 mr-4">
              {getFileIcon(document.file_type)}
              {isRenaming ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    className="flex-1"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRename();
                      if (e.key === 'Escape') setIsRenaming(false);
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={handleRename}
                    disabled={loading || !newTitle.trim()}
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    {t('save')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setIsRenaming(false);
                      setNewTitle(document.title);
                    }}
                  >
                    {t('cancel')}
                  </Button>
                </div>
              ) : (
                <div className="flex-1">
                  <DialogTitle className="text-xl font-semibold flex items-center gap-2">
                    {document.title}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setIsRenaming(true)}
                      className="h-7 w-7 p-0"
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                  </DialogTitle>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(document.created_at)}
                    </div>
                    <div className="flex items-center gap-1">
                      <History className="h-3 w-3" />
                      {document.versions?.length || 1} version(s)
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowVersions(!showVersions)}
                title="Version History"
              >
                <History className="h-4 w-4 mr-1" />
                {t('versions')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownload}
                title="Download"
              >
                <Download className="h-4 w-4 mr-1" />
                {t('download')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDelete}
                disabled={loading}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                title="Delete"
              >
                <X className="h-4 w-4 mr-1" />
                {t('delete')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onClose}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Version History Panel */}
        {showVersions && document.versions && document.versions.length > 0 && (
          <div className="px-6 py-3 bg-blue-50 border-b">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Version History</h3>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {document.versions.map((version, index) => (
                <div key={index} className="flex items-center justify-between text-xs bg-white rounded px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-cyan-600">v{version.version}</span>
                    <span className="text-gray-600">{formatDate(version.created_at)}</span>
                    <span className="text-gray-500">by {version.user_name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-auto px-6 pb-6">
          {renderContent()}
        </div>

        {/* Footer Info */}
        <div className="px-6 py-3 border-t bg-gray-50 text-xs text-gray-600">
          <div className="flex items-center justify-between">
            <span>📝 Download to edit in desktop applications</span>
            <span className="text-cyan-600">💡 Collaborative editing coming soon</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentPreviewDialog;
